import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { isLoggedIn } from '../../utils/auth';

// Get the base URL and auth header
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4611/auth';

export const clientApi = createApi({
  reducerPath: 'clientApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}`,
    prepareHeaders: (headers) => {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (token && isLoggedIn()) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Clients', 'ClientMessages', 'ClientMessageHistory'],
  endpoints: (builder) => ({
    getClients: builder.query({
      query: (params = {}) => {
        // Create URL with query parameters
        const { search, page, limit } = params;
        
        // Build query string
        const queryParams = new URLSearchParams();
        
        // Add pagination parameters if provided
        if (page) queryParams.append('page', page);
        if (limit) queryParams.append('limit', limit);
        
        // Add search parameter if provided
        if (search) queryParams.append('search', search);
        
        // Construct the URL with query parameters
        const queryString = queryParams.toString();
        return `/clients${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Clients'],
    }),
    getClient: builder.query({
      query: (clientId) => `/clients/${clientId}`,
      providesTags: (result, error, clientId) => [{ type: 'Clients', id: clientId }],
    }),
    getClientMessages: builder.query({
      query: (params = {}) => {
        // Create URL with query parameters
        const { clientId, search, page, limit } = params;
        
        // Build query string
        const queryParams = new URLSearchParams();
        
        // Add pagination parameters if provided
        if (page) queryParams.append('page', page);
        if (limit) queryParams.append('limit', limit);
        
        // Add search parameter if provided
        if (search) queryParams.append('search', search);
        
        // Construct the URL with query parameters
        const queryString = queryParams.toString();
        return `/clients/messages/${clientId}${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, clientId) => [
        { type: 'ClientMessages', id: clientId },
      ],
    }),
    getClientMessageHistory: builder.query({
      query: ({clientId, messageId}) => `/clients/messages/${clientId}/${messageId}`,
      providesTags: (result, error, messageId) => [
        { type: 'ClientMessageHistory', id: messageId },
      ],
    }),
    regenerateClientMessage: builder.mutation({
      query: ({ clientId, messageId }) => ({
        url: `/clients/messages/${clientId}/${messageId}`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, { clientId, messageId }) => [
        { type: 'ClientMessages', id: clientId },
        { type: 'ClientMessageHistory', id: messageId },
      ],
    }),
  }),
});

export const {
  useGetClientsQuery,
  useGetClientQuery,
  useGetClientMessagesQuery,
  useGetClientMessageHistoryQuery,
  useRegenerateClientMessageMutation,
} = clientApi; 